/**
 * Visual-effect feature flags. Default ON. Flip to false (or hot-toggle via the helper)
 * while iterating on a specific effect so it doesn't fight other work in progress.
 *
 * Toggle at runtime from the browser console for quick iteration:
 *   window.__JB_EFFECTS__.HR_BALL = false   // disable
 *   window.__JB_EFFECTS__.HR_BALL = true    // enable
 *
 * Components read via `useEffectFlag('HR_BALL')` so a console flip re-renders consumers.
 */
import { useEffect, useState } from "react";

const DEFAULTS = {
  HR_BALL: true,
};

// Mutable runtime store, exposed on window for dev tweaking. Each entry has a Set of
// subscribers that are notified when the flag flips.
const flags = { ...DEFAULTS };
const subscribers = new Map(); // name → Set<callback>

function notify(name) {
  const subs = subscribers.get(name);
  if (subs) subs.forEach(fn => fn(flags[name]));
}

if (typeof window !== "undefined") {
  // Wrap in a Proxy so `window.__JB_EFFECTS__.HR_BALL = false` triggers re-renders.
  window.__JB_EFFECTS__ = new Proxy(flags, {
    set(target, key, value) {
      target[key] = value;
      notify(key);
      return true;
    },
  });
}

export function useEffectFlag(name) {
  const [value, setValue] = useState(flags[name]);
  useEffect(() => {
    let subs = subscribers.get(name);
    if (!subs) {
      subs = new Set();
      subscribers.set(name, subs);
    }
    subs.add(setValue);
    return () => subs.delete(setValue);
  }, [name]);
  return value;
}

export function getEffectFlag(name) {
  return flags[name];
}
